<template>
    <MainComponent data-testid="profile-reputation">
        <template #title>
            <h2>{{ t('profile.section.reputation.title') }}</h2>
        </template>
        <template v-if="profile?.editMode && hasReputation" #title-action>
            <VJoyIconButton icon="pen" variant="ghost" size="small" :label="$t('general.edit')" @click="editReputationWidgets" />
        </template>
        <template #content>
            <FetchErrorHighlight v-if="error">
                <template #technical-error>{{ error }}</template>
            </FetchErrorHighlight>
            <template v-else>
                <div v-if="hasReputation" class="profile-reputation-widgets">
                    <div v-for="reputation in reputations" :key="reputation.providerName" class="profile-reputation-widgets__widget">
                        <a
                            :href="reputation.externalUserPageUrl"
                            :target="getTarget(reputation)"
                            rel="nofollow"
                            @click="onReputationClicked(reputation)"
                        >
                            <div class="profile-reputation-widgets__widget-logo">
                                <ReputationLogo :reputation="reputation" />
                            </div>
                        </a>
                        <ReputationStat :reputation="reputation" />
                    </div>
                </div>
                <div v-else-if="!$ff('profile-revamp')" class="profile-reputation-widgets">
                    <div class="profile-reputation-widgets__subtitle">{{ t('profile.reputation.widgets.empty.subtitle') }}</div>
                </div>
                <EmptyContentSection
                    v-else-if="$ff('profile-revamp')"
                    icon="strategy"
                    :title="$t('profile.section.reputation.empty.title')"
                    :description="$t('profile.section.reputation.empty.subtitle')"
                >
                    <template #action>
                        <ClientOnly>
                            <VJoyButton icon="add" size="small" variant="secondary" @click="editReputationWidgets">
                                {{ $t('profile.section.reputation.empty.cta.label') }}
                            </VJoyButton>
                            <template #fallback>
                                <VJoyFormFieldSkeleton size="small" width="250px" />
                            </template>
                        </ClientOnly>
                    </template>
                </EmptyContentSection>
            </template>
        </template>
    </MainComponent>
</template>

<script setup lang="ts">
    import {storeToRefs} from 'pinia';
    import {computed, useAsyncData, useTranslation} from '#imports';
    import {VJoyButton, VJoyIconButton, VJoyFormFieldSkeleton} from '@maltjoy/core-vue';
    import MainComponent from '~/components/main/common/MainComponent.vue';
    import EmptyContentSection from '~/components/section/EmptyContentSection.vue';
    import {useProfileTracking} from '~/composables/useProfileTracking';
    import {eventBus} from '~/config/events';
    import {EditionOpenedEntryPoint, InfluenceLinkType} from '~/config/TrackingEvents';
    import ReputationLogo from '~/features/reputation-widgets/ReputationLogo.vue';
    import ReputationStat from '~/features/reputation-widgets/ReputationStat.vue';
    import {useProfileStore} from '~/store/profileStore';
    import {useReputationStore} from '~/store/reputation/reputationStore';
    import type {ExternalReputationResource} from '~/types/api-raw-types';
    import FetchErrorHighlight from '@/components/_common/FetchErrorHighlight.vue';

    const {t} = useTranslation();
    const {profile} = storeToRefs(useProfileStore());
    const hasReputation = computed(() => reputations.value.length > 0);
    const {reputations} = storeToRefs(useReputationStore());
    const {getReputations} = useReputationStore();
    const {trackInfluenceLinkClicked} = useProfileTracking();

    function getTarget(reputation: ExternalReputationResource): string {
        return reputation.openLinkInCurrentTab ? '_self' : '_blank';
    }

    function editReputationWidgets() {
        eventBus.emit('reputation:edit-mode', {entryPoint: EditionOpenedEntryPoint.PROFILE_PAGE});
    }

    function onReputationClicked(reputation: ExternalReputationResource) {
        trackInfluenceLinkClicked(getInfluenceLinkType(reputation), profile.value!.profileId);
    }

    function getInfluenceLinkType(reputation: ExternalReputationResource): InfluenceLinkType {
        switch (reputation.providerName) {
            case 'behance':
                return InfluenceLinkType.BEHANCE;
            case 'github':
                return InfluenceLinkType.GITHUB;
            case 'stackoverflow':
                return InfluenceLinkType.STACKOVERFLOW;
            case 'kaggle':
                return InfluenceLinkType.KAGGLE;
        }
        throw new Error(`Unknown reputation provider name: ${reputation.providerName}`);
    }

    const {error} = await useAsyncData(
        'left_reputation-widget',
        async () => {
            const reputations = await getReputations();
            return {reputations};
        },
        {deep: true},
    );
</script>

<style lang="scss" scoped>
    .line-breaker {
        padding: 0 !important;
        flex-basis: 100% !important;
    }

    .profile-reputation-widgets {
        display: flex;
        flex-direction: column;

        &__widget {
            display: flex;
            padding: 20px 0;

            &:is(:first-child) {
                padding-top: 0;
            }

            &:is(:last-child) {
                padding-bottom: 0;
            }

            &:not(:last-child) {
                border-bottom: 1px solid var(--joy-color-neutral-30);
            }
        }
    }
</style>
